import { TASK_REMINDER_OFFSETS } from '@shared/constants/business/task';
import { taskReminderOffsetSchema } from '@shared/schemas/task-reminders';
import type { ClaimStatesDisplaySnapshot, ClaimStatesSnapshot } from '@shared/types/claim-states';
import { z } from 'zod';

// ── Snapshot runtime validator ───────────────────────────────────────────────
// Prisma's JSON typing is compile-time only, so any code that drives logic off
// `Claim.statesSnapshot` (advance, seeding, the bar's render guard) parses it
// with this schema first. A parse failure is treated as "no states".

const claimStateTaskDefinitionSnapshotSchema = z.object({
    definitionKey: z.string(),
    taskListKey: z.string(),
    name: z.string(),
    description: z.string().optional(),
    dueDateOffset: z.number(),
    reminderOffsets: z.array(taskReminderOffsetSchema).max(TASK_REMINDER_OFFSETS.length).optional(),
});

const claimStateDisplayItemSchema = z.object({
    key: z.string(),
    name: z.string(),
    position: z.number().int().min(0),
});

const claimStateSnapshotItemSchema = claimStateDisplayItemSchema.extend({
    // Default preserves compatibility with snapshots written before frozen
    // task-list membership was added to the version-1 payload.
    taskListKeys: z.array(z.string()).default([]),
    taskDefinitions: z.array(claimStateTaskDefinitionSnapshotSchema),
});

function validateStateSequence(states: Array<{ key: string; position: number }>, ctx: z.RefinementCtx): void {
    const keys = new Set<string>();
    states.forEach((state, index) => {
        if (keys.has(state.key)) {
            ctx.addIssue({
                code: 'custom',
                message: 'State keys must be unique.',
                path: [index, 'key'],
            });
        }
        keys.add(state.key);

        if (state.position !== index) {
            ctx.addIssue({
                code: 'custom',
                message: 'State positions must match their array index.',
                path: [index, 'position'],
            });
        }
    });
}

export const claimStatesSnapshotSchema = z.object({
    version: z.literal(1),
    states: z.array(claimStateSnapshotItemSchema).superRefine(validateStateSequence),
}) satisfies z.ZodType<ClaimStatesSnapshot>;

export const claimStatesDisplaySnapshotSchema = z.object({
    version: z.literal(1),
    states: z.array(claimStateDisplayItemSchema).superRefine(validateStateSequence),
}) satisfies z.ZodType<ClaimStatesDisplaySnapshot>;

// ── Config input schemas (product-type states CRUD) ──────────────────────────
// Shared so the client config forms and the server routes validate identically.
// `taskListIds` has no `.min(1)` — a state may deliberately drop zero tasks.

export const productTypeStateCreateInputSchema = z
    .object({
        productTypeId: z.bigint(),
        name: z.string().trim().min(1).max(120),
        taskListIds: z.array(z.bigint()),
    })
    .strict();

export const productTypeStateUpdateInputSchema = z
    .object({
        id: z.bigint(),
        set: z
            .object({
                name: z.string().trim().min(1).max(120).optional(),
                taskListIds: z.array(z.bigint()).optional(),
            })
            .strict()
            .refine((set) => set.name !== undefined || set.taskListIds !== undefined, {
                message: 'At least one field must be provided.',
            }),
    })
    .strict();

export const productTypeStateReorderInputSchema = z
    .object({
        productTypeId: z.bigint(),
        orderedIds: z
            .array(z.bigint())
            .refine((ids) => new Set(ids).size === ids.length, { message: 'IDs must be unique.' }),
    })
    .strict();

export const advanceClaimStateInputSchema = z
    .object({
        id: z.bigint(),
        /** Required — drives the compare-and-swap that makes double-advance impossible. */
        expectedFromPosition: z.number().int().min(0),
    })
    .strict();

export type ProductTypeStateCreateInput = z.infer<typeof productTypeStateCreateInputSchema>;
export type ProductTypeStateUpdateInput = z.infer<typeof productTypeStateUpdateInputSchema>;
export type ProductTypeStateReorderInput = z.infer<typeof productTypeStateReorderInputSchema>;
export type AdvanceClaimStateInput = z.infer<typeof advanceClaimStateInputSchema>;

// ── Pure derivations (shared by the client bar and the server) ───────────────

export type ClaimStateClassification = 'completed' | 'current' | 'upcoming';

/** Positional state of the claim, used to classify each snapshot step. */
export type ClaimStatePositionState = {
    currentStatePosition: number | null;
    statesCompletedAt: Date | string | null;
};

/**
 * Classifies one snapshot position relative to the claim's progress. Once the
 * claim is terminal (`statesCompletedAt` set), every position reads `completed`.
 */
export function classifyState(position: number, claim: ClaimStatePositionState): ClaimStateClassification {
    if (claim.statesCompletedAt != null) {
        return 'completed';
    }
    if (claim.currentStatePosition != null && position < claim.currentStatePosition) {
        return 'completed';
    }
    if (position === claim.currentStatePosition) {
        return 'current';
    }
    return 'upcoming';
}

/**
 * Render/logic guard: parses an untyped `statesSnapshot` and returns it only
 * when it is valid AND non-empty, else `null`. Client and server share this so a
 * corrupt snapshot degrades identically (no bar client-side, no-op server-side).
 */
export function hasClaimStates(snapshot: unknown): ClaimStatesSnapshot | null {
    const parsed = claimStatesSnapshotSchema.safeParse(snapshot);
    if (!parsed.success || parsed.data.states.length === 0) {
        return null;
    }
    return parsed.data;
}

/** Converts a valid persisted snapshot to the display-only claim-read contract. */
export function toClaimStatesDisplaySnapshot(snapshot: unknown): ClaimStatesDisplaySnapshot | null {
    const parsed = claimStatesSnapshotSchema.safeParse(snapshot);
    if (!parsed.success) {
        return null;
    }

    return {
        version: parsed.data.version,
        states: parsed.data.states.map(({ key, name, position }) => ({ key, name, position })),
    };
}

/** Render guard for the display-only claim-state sequence. */
export function hasClaimStatesDisplaySnapshot(snapshot: unknown): ClaimStatesDisplaySnapshot | null {
    const parsed = claimStatesDisplaySnapshotSchema.safeParse(snapshot);
    if (!parsed.success || parsed.data.states.length === 0) {
        return null;
    }
    return parsed.data;
}
